<template>
  <section class="container2">
    <div
      :style="backgroundStyle"
      :class="{ 'max-lg:overflow-hidden': data.table_of_content }"
      class="relative"
    >
      <template v-if="data.background_type === RichTextBackgroundTypes.IMAGE">
        <UiVideo
          v-if="data?.file?.media_type?.includes(assetsTypes.video)"
          class="h-dynamic-image-mobile w-full object-cover lg:h-dynamic-image"
          :class="[data.height_desktop ? '' : 'absolute inset-0 h-full']"
          :src="data?.file?.url"
          :src-mobile="data?.fileMobile?.url"
          :style="{
            '--image-height': data.height_desktop || '100%',
            '--mobile-image-height':
              data.height_mobile || data.height_desktop || '100%',
          }"
          autoplay
          loop
          muted
          playsinline
        />
        <UiLazyImg
          v-else
          class="h-dynamic-image-mobile w-full lg:h-dynamic-image"
          :class="[data.height_desktop ? '' : 'absolute inset-0 h-full']"
          :src="data.file?.url || ''"
          :src-mobile="data.fileMobile?.url || ''"
          :alt="data.file?.alt || removeHTMLTagFromString(data.title) || ''"
          cover
          :no-lazy="!blockIndex"
          :img-classes="'object-cover'"
          :img-style="`object-position: ${data.image_position || 'center'}`"
          :style="{
            '--image-height': data.height_desktop || '100%',
            '--mobile-image-height':
              data.height_mobile || data.height_desktop || '100%',
          }"
        />
      </template>
      <div
        class="container"
        :class="[
          Object.values(backgroundStyle).length ? 'lg:px-18' : '',
          data.height_desktop &&
          data.background_type === RichTextBackgroundTypes.IMAGE
            ? 'absolute inset-0 h-full'
            : 'relative',
        ]"
      >
        <h2
          v-if="data.title"
          class="title-underline mb-5 flex justify-center pb-4 text-base font-normal tracking-wider text-gray-disabled"
          v-html="data.title"
        />
        <div v-if="data.table_of_content" class="lg:flex lg:gap-x-20">
          <div
            class="table-of-content w-full lg:max-w-[360px] lg:flex-[0_0_360px]"
            @click="contentLink"
            v-html="data.table_of_content"
          ></div>
          <div
            v-if="data.content"
            ref="target"
            class="content lg:grow"
            @click="contentLink"
            v-html="formattedContent"
          ></div>
        </div>
        <div
          v-else-if="data.content"
          ref="target"
          class="content"
          @click="contentLink"
          v-html="isShowMoreLessEnabled ? editedText : formattedContent"
        ></div>
      </div>
    </div>
    <div v-if="isShowMoreLessEnabled" class="container pb-10 pt-2 lg:pb-16">
      <UiButton
        variant="text"
        class="pl-0 underline"
        @click="showMore = !showMore"
      >
        {{ showMore ? $t("showMore") : $t("showLess") }}
      </UiButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { parse } from "node-html-parser";
import {
  assetsTypes,
  RichTextBackgroundTypes,
  RichTextModuleData,
} from "ecom-types";
const props = withDefaults(
  defineProps<{
    data: RichTextModuleData;
    blockIndex?: number;
    blockName?: string;
  }>(),
  {
    blockIndex: -1,
    blockName: "",
  },
);
const contentLink = useContentLink;
const { isShowMoreLessEnabled, showMore, editedText } = useShowMoreLess();
const target = ref<HTMLElement | null>(null);

const backgroundStyle = computed(() => {
  switch (props.data.background_type) {
    case RichTextBackgroundTypes.COLOR: {
      return {
        backgroundColor:
          props.data.color.hexa || props.data.color || "transparent",
      };
    }
    case RichTextBackgroundTypes.CUSTOM: {
      return {
        background: props.data.custom_background,
      };
    }
    default: {
      return {};
    }
  }
});

const formattedContent = computed(() => {
  // Define a regular expression to match script tags with a function
  const scriptTagRegex = /<script\b[^>]*>*<\/script>/gi;
  // Use the replace method to remove script tags with functions
  const modifiedHtmlString = props.data.content.replace(scriptTagRegex, "");
  return modifiedHtmlString;
});

onMounted(() => {
  const scripts = extractDataFromScriptTags(props.data.content);
  const targetElement = target.value;

  if (targetElement) {
    scripts.forEach((script) => {
      addScripts(script, targetElement, "end");
    });
  }
});

function useShowMoreLess() {
  const showMore = ref(true);
  const isShowMoreLessEnabled = computed(() => {
    return props.blockName?.includes("show-more-less");
  });
  const editedText = computed(() => {
    return showMore.value
      ? extractContentUpToSecondP(props.data.content)
      : props.data.content;
  });

  function extractContentUpToSecondP(html: string): string {
    const root = parse(html);
    const paragraphs = root.querySelectorAll("p");
    if (paragraphs.length < 2) {
      return html;
    }
    const secondPIndex =
      html.indexOf(paragraphs[1].toString()) + paragraphs[1].toString().length;
    return html.substring(0, secondPIndex);
  }
  return {
    isShowMoreLessEnabled,
    showMore,
    editedText,
  };
}
</script>

<style scoped>
.content:deep(.button) {
  @apply flex max-w-fit items-center justify-center bg-button-primary px-[25px] py-4 font-futura text-sm uppercase text-white outline-0 transition-all hover:bg-button-primary-hover;
}
.content:deep(.button.outline) {
  @apply border !border-button-secondary !bg-transparent !text-button-secondary hover:!bg-button-secondary hover:!text-white;
}

.content:deep(.link-underline) {
  a {
    @apply underline;
  }
}
</style>
